import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Checkbox,
  message,
  Row,
  Col,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clinicService, configService } from "../../services";
import { ClinicConfig, EInvoiceConfig, MedicalFacilityConfigRequest } from "../../types";
import "./ClinicConfigPage.css";
import { useAuthStore } from "../../stores/authStore";
import ProvinceSelector from "../../components/ProvinceSelector";
import WardSelector from "../../components/WardSelector";

const { Option } = Select;

const ClinicConfigPage: React.FC = () => {
  const [clinicInfoForm] = Form.useForm();
  const [nationalPrescriptionForm] = Form.useForm();
  const [clinicInvoiceForm] = Form.useForm();
  const [pharmacyInvoiceForm] = Form.useForm();
  const queryClient = useQueryClient();
  const [currentProvinceCode, setCurrentProvinceCode] = useState<string | undefined>(undefined);
  const user = useAuthStore((state) => {
    return state.user;
  });

  const { data: medicalFacilityConfig } = useQuery({
    queryKey: ["medical-facilities-config"],
    queryFn: () => configService.getMedicalFacilityConfig(),
  });

  const { data: eInvoiceConfig } = useQuery({
    queryKey: ["e-invoice-config"],
    queryFn: () => configService.getEInvoiceConfig("medical_facility"),
  });

  const { data: pharmacyInvoiceConfig } = useQuery({
    queryKey: ["pharmacy-invoice-config"],
    queryFn: () => configService.getEInvoiceConfig("pharmacy"),
  });

  const { data: clinicDetail } = useQuery({
    queryKey: ["clinic-detail"],
    queryFn: () => clinicService.clinicDetail(),
  });

  const handleProvinceChange = (value: string) => {
    console.log("[DEBUG] (👉ﾟヮﾟ)👉  ~ handleProvinceChange  ~ value:", value)
    setCurrentProvinceCode(value);
  };

  // Mutation for clinic info section
  const saveClinicInfoMutation = useMutation({
    mutationFn: (values: Partial<ClinicConfig>) => {
      return "" as any;
      // const updatedConfig = { ...config, ...values } as ClinicConfig;
      // return configService.saveClinicConfig(updatedConfig);
    },
    onSuccess: () => {
      message.success("Lưu thông tin phòng khám thành công!");
      queryClient.invalidateQueries({ queryKey: ["clinic-config"] });
    },
    onError: () => {
      message.error("Lưu thông tin phòng khám thất bại!");
    },
  });

  // Mutation for national prescription section
  const saveNationalPrescriptionMutation = useMutation({
    mutationFn: (values: Partial<MedicalFacilityConfigRequest>) => {
      const updatedConfig = { ...values } as MedicalFacilityConfigRequest;
      return configService.saveMedicalFacilityConfig(updatedConfig);
    },
    onSuccess: () => {
      message.success("Lưu cấu hình đơn thuốc quốc gia thành công!");
      queryClient.invalidateQueries({ queryKey: ["clinic-config"] });
    },
    onError: () => {
      message.error("Lưu cấu hình đơn thuốc quốc gia thất bại!");
    },
  });

  // Mutation for clinic invoice section
  const saveClinicInvoiceMutation = useMutation({
    mutationFn: (values: EInvoiceConfig) => {
      return configService.saveEInvoiceConfig({
        ...values,
        type: 'medical_facility',
      });
    },
    onSuccess: () => {
      message.success("Lưu cấu hình hóa đơn điện tử phòng khám thành công!");
      queryClient.invalidateQueries({ queryKey: ["clinic-config"] });
    },
    onError: () => {
      message.error("Lưu cấu hình hóa đơn điện tử phòng khám thất bại!");
    },
  });

  // Mutation for pharmacy invoice section
  const savePharmacyInvoiceMutation = useMutation({
    mutationFn: (values: EInvoiceConfig) => {
      return configService.saveEInvoiceConfig({
        ...values,
        type: 'pharmacy',
      });
      // const updatedConfig = { ...config, ...values } as ClinicConfig;
      // return configService.saveClinicConfig(updatedConfig);
    },
    onSuccess: () => {
      message.success("Lưu cấu hình hóa đơn điện tử nhà thuốc thành công!");
      queryClient.invalidateQueries({ queryKey: ["clinic-config"] });
    },
    onError: () => {
      message.error("Lưu cấu hình hóa đơn điện tử nhà thuốc thất bại!");
    },
  });

  useEffect(() => {
    if (medicalFacilityConfig) {
      nationalPrescriptionForm.setFieldsValue({
        pk_code: medicalFacilityConfig?.data?.data?.pk_code,
        pk_username: medicalFacilityConfig?.data?.data?.pk_username,
        rx_prefix: medicalFacilityConfig?.data?.data?.rx_prefix,
        pk_password: medicalFacilityConfig?.data?.data?.pk_password,
        is_enabled: medicalFacilityConfig?.data?.data?.is_enabled,
      });
    }
  }, [nationalPrescriptionForm, medicalFacilityConfig]);

  useEffect(() => {
    if (eInvoiceConfig) {
      clinicInvoiceForm.setFieldsValue({
        api_server: eInvoiceConfig?.data?.data?.api_server,
        username: eInvoiceConfig?.data?.data?.username,
        password: eInvoiceConfig?.data?.data?.password,
        dai_gtgt: eInvoiceConfig?.data?.data?.dai_gtgt,
        is_enabled: eInvoiceConfig?.data?.data?.is_enabled,
      });
    }
  }, [clinicInvoiceForm, eInvoiceConfig]);

  useEffect(() => {
    if (pharmacyInvoiceConfig) {
      pharmacyInvoiceForm.setFieldsValue({
        api_server: pharmacyInvoiceConfig?.data?.data?.api_server,
        username: pharmacyInvoiceConfig?.data?.data?.username,
        password: pharmacyInvoiceConfig?.data?.data?.password,
        dai_gtgt: pharmacyInvoiceConfig?.data?.data?.dai_gtgt,
        is_enabled: pharmacyInvoiceConfig?.data?.data?.is_enabled,
      });
    }
  }, [pharmacyInvoiceForm, pharmacyInvoiceConfig]);


  useEffect(() => {
    if (clinicDetail) {
      setCurrentProvinceCode(clinicDetail?.data?.province_code);
      clinicInfoForm.setFieldsValue({
        name: clinicDetail?.data?.name,
        tax_code: clinicDetail?.data?.tax_code,
        phone: clinicDetail?.data?.phone,
        province_code: clinicDetail?.data?.province_code,
        ward_code: clinicDetail?.data?.ward_code,
        hamlet: clinicDetail?.data?.hamlet,
        street: clinicDetail?.data?.street,
      });
    }
  }, [clinicInfoForm, clinicDetail]);
  const onClinicInfoFinish = (values: any) => {
    saveClinicInfoMutation.mutate(values);
  };

  const onNationalPrescriptionFinish = (values: any) => {
    saveNationalPrescriptionMutation.mutate({
      ...values,
      medical_facility: user?.medical_facility_id,
    });
  };

  const onClinicInvoiceFinish = (values: any) => {
    saveClinicInvoiceMutation.mutate(values);
  };

  const onPharmacyInvoiceFinish = (values: any) => {
    savePharmacyInvoiceMutation.mutate(values);
  };

  return (
    <div className="clinic-config-page">
      {/* Thông tin phòng khám */}
      <Card title="Thông tin phòng khám" className="config-section">
        <Form
          form={clinicInfoForm}
          layout="vertical"
          onFinish={onClinicInfoFinish}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên phòng khám"
                rules={[
                  { required: true, message: "Vui lòng nhập tên phòng khám!" },
                ]}
              >
                <Input placeholder="Nhập tên phòng khám" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="tax_code"
                label="Mã số thuế"
                rules={[
                  { required: true, message: "Vui lòng nhập mã số thuế!" },
                ]}
              >
                <Input placeholder="Nhập mã số thuế" />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="phone"
                label="Điện thoại"
                rules={[
                  { required: true, message: "Vui lòng nhập số điện thoại!" },
                ]}
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="province_code" label="Tỉnh / Thành phố">
                <ProvinceSelector onChange={handleProvinceChange} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name="ward_code" label="Phường / Xã">
                <WardSelector provinceCode={currentProvinceCode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="hamlet" label="Khu phố / Ấp">
                <Select placeholder="Vui lòng chọn khu phố">
                  <Option value="hamlet1">Khu phố 1</Option>
                  <Option value="hamlet2">Khu phố 2</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="street" label="Số nhà / Tên đường">
                <Input placeholder="Nhập số nhà / tên đường" />
              </Form.Item>
            </Col>
          </Row>

          <div className="section-actions">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => clinicInfoForm.submit()}
              loading={saveClinicInfoMutation.isPending}
            >
              Lưu thông tin
            </Button>
          </div>
        </Form>
      </Card>

      {/* Cấu hình đơn thuốc quốc gia */}
      <Row className="clinic-config-section">
        <Col span={8}>
          <Card title="Cấu hình đơn thuốc quốc gia" className="config-section">
            <Form
              form={nationalPrescriptionForm}
              layout="vertical"
              onFinish={onNationalPrescriptionFinish}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="pk_code"
                    label="Mã liên thông PK"
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng nhập mã liên thông PK!",
                      },
                    ]}
                  >
                    <Input placeholder="Nhập mã liên thông PK" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="pk_username"
                    label="Tài khoản PK"
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng nhập tài khoản PK!",
                      },
                    ]}
                  >
                    <Input placeholder="Nhập tài khoản PK" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="pk_password"
                    label="Mật khẩu PK"
                  >
                    <Input.Password placeholder="Nhập mật khẩu PK" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="rx_prefix"
                    label="Tiền tố mã toa"
                    rules={[
                      {
                        required: true,
                        message: "Vui lòng nhập tiền tố mã toa!",
                      },
                    ]}
                  >
                    <Input placeholder="Nhập tiền tố mã toa" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="is_enabled" valuePropName="checked">
                <Checkbox>Hiển thị chức năng</Checkbox>
              </Form.Item>

              <div className="section-actions">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => nationalPrescriptionForm.submit()}
                  loading={saveNationalPrescriptionMutation.isPending}
                >
                  Lưu thông tin
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
        <Col span={8}>
          {/* Cấu hình hóa đơn điện tử phòng khám */}
          <Card
            title="Cấu hình hóa đơn điện tử phòng khám"
            className="config-section"
          >
            <Form
              form={clinicInvoiceForm}
              layout="vertical"
              onFinish={onClinicInvoiceFinish}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="api_server"
                    label="API Server"
                    rules={[
                      { required: true, message: "Vui lòng nhập API Server!" },
                    ]}
                  >
                    <Input placeholder="Nhập API Server" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="username"
                    label="Tài khoản"
                    rules={[
                      { required: true, message: "Vui lòng nhập tài khoản!" },
                    ]}
                  >
                    <Input placeholder="Nhập tài khoản" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="password"
                    label="Mật khẩu"
                  >
                    <Input.Password placeholder="Nhập mật khẩu" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="dai_gtgt"
                    label="Dải GTGT"
                    rules={[
                      { required: true, message: "Vui lòng nhập dải GTGT!" },
                    ]}
                  >
                    <Input placeholder="Nhập dải GTGT" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="is_enabled" valuePropName="checked">
                <Checkbox>Hiển thị chức năng</Checkbox>
              </Form.Item>

              <div className="section-actions">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => clinicInvoiceForm.submit()}
                  loading={saveClinicInvoiceMutation.isPending}
                >
                  Lưu thông tin
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
        <Col span={8}>
          {/* Cấu hình hóa đơn điện tử nhà thuốc */}
          <Card
            title="Cấu hình hóa đơn điện tử nhà thuốc"
            className="config-section"
          >
            <Form
              form={pharmacyInvoiceForm}
              layout="vertical"
              onFinish={onPharmacyInvoiceFinish}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="api_server"
                    label="API Server"
                    rules={[
                      { required: true, message: "Vui lòng nhập API Server!" },
                    ]}
                  >
                    <Input placeholder="Nhập API Server" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="username"
                    label="Tài khoản"
                    rules={[
                      { required: true, message: "Vui lòng nhập tài khoản!" },
                    ]}
                  >
                    <Input placeholder="Nhập tài khoản" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="password"
                    label="Mật khẩu"
                  >
                    <Input.Password placeholder="Nhập mật khẩu" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="dai_gtgt"
                    label="Dải GTGT"
                    rules={[
                      { required: true, message: "Vui lòng nhập dải GTGT!" },
                    ]}
                  >
                    <Input placeholder="Nhập dải GTGT" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="is_enabled" valuePropName="checked">
                <Checkbox>Hiển thị chức năng</Checkbox>
              </Form.Item>

              <div className="section-actions">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => pharmacyInvoiceForm.submit()}
                  loading={savePharmacyInvoiceMutation.isPending}
                >
                  Lưu thông tin
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClinicConfigPage;
